import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Search, Pen, AlertTriangle, X, ArrowRightLeft, Plus } from 'lucide-react';
import { StockLocationBar } from '../../components/StockLocationBar';
import { CustomSelect } from '../../components/CustomSelect';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { SideDrawer } from '../../components/SideDrawer';
import {
  resolveStockScopes, effectiveBranchesOf, CENTRAL_LOCATION,
    stockRowsOf, findCatalogProduct, variantLabelOf, updateStockRow,
    addStockRow, catalogProductsOf,
} from '../../data/store';
import { IconAction, RowActions } from '../../components/RowActions';

export const InventoryActiveTab = ({ org, showNotification, canEdit, lockedBranchId, isT1, hideEmergAndDelivery }) => {
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [transferring, setTransferring] = useState(null);

  const products = useMemo(() => catalogProductsOf(org.id), [org.id, version]);

  const scopes = useMemo(() => resolveStockScopes(org.id), [org.id]);
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id]);

  const resolveLocation = () => {
    if (scopes.inventoryScope === 'central') return CENTRAL_LOCATION;
    if (lockedBranchId) return lockedBranchId;
    return branches[0]?.id || '';
  };
  const [locationId, setLocationId] = useState(resolveLocation);
  useEffect(() => {
    setLocationId(resolveLocation());
  }, [scopes.inventoryScope, branches, lockedBranchId]);

  const rows = useMemo(() => {
    const all = stockRowsOf(org.id, locationId, 'active');
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((r) => {
      const p = findCatalogProduct(r.productId);
      const v = (p?.variants || []).find((varItem) => varItem.id === r.variantId);
      const barcode = r.barcode || v?.barcode || p?.barcode || '';
      return p && (
        (p.name || '').toLowerCase().includes(q) ||
        barcode.toLowerCase().includes(q) ||
        (r.batchNumber || '').toLowerCase().includes(q)
      );
    });
  }, [search, org.id, locationId, version]);

  const accent = org.theme.primary;

  const saveEdit = () => {
    if (!editing) return;
    updateStockRow(editing.id, {
      qty: Number(editing.qty) || 0,
      sellPrice: Number(editing.sellPrice) || 0,
      minStockThreshold: Number(editing.minStockThreshold) || 0,
      batchNumber: editing.batchNumber || '',
      barcode: editing.barcode || '',
      expiryDate: editing.expiryDate || '',
    });
    if (showNotification) showNotification('Inventory updated.', 'success');
    setEditing(null);
    setVersion((v) => v + 1);
  };


  const handleTransfer = () => {
    if (!transferring) return;
    const tQty = Number(transferring.transferQty) || 0;
    if (tQty <= 0 || tQty > transferring.qty) {
      if (showNotification) showNotification('Invalid transfer quantity.', 'error');
      return;
    }
    const finalReason = transferring.reason === 'Custom' ? transferring.customReason : transferring.reason;
    if (!finalReason) {
      if (showNotification) showNotification('Please provide a reason.', 'error');
      return;
    }

    // Update active row
    updateStockRow(transferring.id, { qty: transferring.qty - tQty });

    const existingRows = stockRowsOf(org.id, transferring.locationId);
    const existingPending = existingRows.find(r =>
      r.status === 'pending' &&
      r.productId === transferring.productId &&
      (r.variantId || '') === (transferring.variantId || '') &&
      (r.note || '') === (finalReason || '')
    );

    if (existingPending) {
      updateStockRow(existingPending.id, { qty: Number(existingPending.qty) + tQty });
    } else {
      addStockRow({
        orgId: org.id,
        locationId: transferring.locationId,
        productId: transferring.productId,
        variantId: transferring.variantId || '',
        qty: tQty,
        costPrice: transferring.costPrice,
        sellPrice: transferring.sellPrice,
        minStockThreshold: transferring.minStockThreshold,
        status: 'pending',
        note: finalReason
      });
    }

    if (showNotification) showNotification('Stock transferred to pending.', 'success');
    setTransferring(null);
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 items-center gap-4">
            <div className="relative flex-1 min-w-[180px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search active stock…"
                className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none" />
            </div>

            <div className="shrink-0">
              <StockLocationBar
                org={org}
                scope={scopes.inventoryScope}
                mode={scopes.mode}
                branches={branches}
                locationId={locationId}
                onChange={setLocationId}
                accent={accent}
                lockedBranchId={lockedBranchId}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-[var(--theme-tertiary)]">
              <tr>
                <th className="px-5 py-3 whitespace-nowrap">Product</th>
                <th className="px-3 py-3 whitespace-nowrap">Var</th>
                <th className="px-3 py-3 whitespace-nowrap text-right">Qty</th>
                <th className="px-3 py-3 whitespace-nowrap text-right">Cost</th>
                <th className="px-3 py-3 whitespace-nowrap text-right">Sell</th>
                <th className="px-3 py-3 whitespace-nowrap text-right">Alt Qty</th>
                <th className="px-3 py-3 whitespace-nowrap text-center">Barcode</th>
                <th className="px-3 py-3 whitespace-nowrap text-left">Batch</th>
                <th className="px-3 py-3 whitespace-nowrap text-left">Exp Date</th>
                <th className="px-3 py-3 whitespace-nowrap text-right">Stock Val</th>
                <th className="px-3 py-3 whitespace-nowrap text-center">Status</th>
                {canEdit && <th className="px-5 py-3 whitespace-nowrap text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-tertiary)]">
              {rows.map((r) => {
                const product = findCatalogProduct(r.productId);
                const variant = (product?.variants || []).find((v) => v.id === r.variantId);
                const barcode = r.barcode || variant?.barcode || product?.barcode || '';
                const minThreshold = Number(r.minStockThreshold) || 0;
                const numericQty = Number(r.qty) || 0;
                const noStock = numericQty === 0;
                const low = numericQty > 0 && numericQty <= minThreshold;

                // Check expiry status against today's date
                const now = new Date();
                let isExpired = false;
                let isExpiringSoon = false;

                if (r.expiryDate) {
                  const parts = r.expiryDate.split('-').map(Number);
                  if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
                    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const diffDays = Math.round((expDate - today) / (1000 * 60 * 60 * 24));
                    if (diffDays < 0) {
                      isExpired = true;
                    } else if (diffDays <= 3) {
                      isExpiringSoon = true;
                    }
                  }
                }

                return (
                  <tr key={r.id} className="hover:bg-slate-50 group">
                    <td className="px-5 py-3 text-[13px] font-semibold text-[#0d0d0d] whitespace-nowrap">
                      {product?.name || r.productId}
                    </td>
                    <td className="px-3 py-3 text-[11px] text-slate-500 whitespace-nowrap">{variantLabelOf(product, r.variantId)}</td>
                    <td className="px-3 py-3 text-[12px] font-bold text-right text-[#0d0d0d] whitespace-nowrap">{r.qty}</td>
                    <td className="px-3 py-3 text-[12px] text-right text-slate-700 whitespace-nowrap">₹{r.costPrice.toFixed(2)}</td>
                    <td className="px-3 py-3 text-[12px] text-right text-slate-700 whitespace-nowrap">₹{r.sellPrice.toFixed(2)}</td>
                    <td className="px-3 py-3 text-[12px] font-bold text-right text-[#0d0d0d] whitespace-nowrap">{minThreshold}</td>
                    <td className="px-3 py-3 text-[12px] font-mono font-medium text-slate-700 text-center whitespace-nowrap">
                      {barcode || '—'}
                    </td>
                    <td className="px-3 py-3 text-[12px] font-mono font-medium text-amber-600 whitespace-nowrap">
                      {r.batchNumber || '—'}
                    </td>
                    <td className={`px-3 py-3 text-[11px] font-mono whitespace-nowrap ${
                      isExpired ? 'text-rose-600 font-bold' : isExpiringSoon ? 'text-amber-600 font-bold' : 'text-slate-700 font-medium'
                    }`}>
                      {r.expiryDate || '—'}
                    </td>
                    <td className="px-3 py-3 text-[12px] font-extrabold text-right text-[#0d0d0d] whitespace-nowrap">₹{(r.qty * r.sellPrice).toFixed(2)}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">
                      {noStock ? (
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-rose-600">Out of Stock</span>
                      ) : isExpired ? (
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-rose-600">Expired</span>
                      ) : isExpiringSoon ? (
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-amber-600">Expire Soon</span>
                      ) : low ? (
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-orange-500">Low Stock</span>
                      ) : (
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-600">In Stock</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-5 py-3 whitespace-nowrap text-right">
                        <RowActions>
                          <IconAction icon={Pen} label={`Edit ${r.name || 'this line'}`} title="Edit" onClick={() => setEditing({ ...r })} />
                          <IconAction
                            icon={ArrowRightLeft}
                            tone="warn"
                            label={`Move ${r.name || 'this line'} back to pending`}
                            title="Transfer to pending"
                            onClick={() => setTransferring({ ...r, transferQty: 1, reason: hideEmergAndDelivery ? 'Unsold product' : 'Emergency product' })}
                          />
                        </RowActions>
                      </td>
                    )}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={canEdit ? 12 : 11} className="px-5 py-16 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">No active stock at this location.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <SideDrawer onClose={() => setEditing(null)}>
          <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <h3 className="text-base font-extrabold text-[#0d0d0d]">Adjust stock</h3>
            <button type="button" onClick={() => setEditing(null)} className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50" aria-label="Close"><X size={18} /></button>
          </header>
          <div className="px-5 py-5 grid grid-cols-2 gap-3 flex-1 overflow-y-auto custom-scrollbar">
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Qty</span>
              <input type="number" value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })} className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Sell (₹)</span>
              <input type="number" step="0.01" value={editing.sellPrice} onChange={(e) => setEditing({ ...editing, sellPrice: e.target.value })} className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Alt Qty</span>
              <input type="number" value={editing.minStockThreshold ?? ''} onChange={(e) => setEditing({ ...editing, minStockThreshold: e.target.value })} className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Batch Number</span>
              <input type="text" value={editing.batchNumber || ''} onChange={(e) => setEditing({ ...editing, batchNumber: e.target.value })} placeholder="e.g. BATCH-01" className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-mono font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Branch Barcode</span>
              <input type="text" value={editing.barcode || ''} onChange={(e) => setEditing({ ...editing, barcode: e.target.value })} placeholder="Branch sticker code" className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-mono font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1 col-span-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Expiry Date</span>
              <CustomDatePickerFixed value={editing.expiryDate || ''} onChange={(val) => setEditing({ ...editing, expiryDate: val })} />
            </label>
          </div>
          <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
            <button type="button" onClick={saveEdit} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>Save</button>
          </footer>
        </SideDrawer>
      )}


      {transferring && (
        <SideDrawer onClose={() => setTransferring(null)}>
          <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <h3 className="text-base font-extrabold text-[#0d0d0d]">Transfer to Pending</h3>
            <button type="button" onClick={() => setTransferring(null)} className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50" aria-label="Close"><X size={18} /></button>
          </header>
          <div className="px-5 py-5 flex flex-col gap-4 flex-1 overflow-y-auto custom-scrollbar">
            <div className="text-[13px] font-bold text-[#0d0d0d]">
              Product: {findCatalogProduct(transferring.productId)?.name} {transferring.variantId ? `(${variantLabelOf(findCatalogProduct(transferring.productId), transferring.variantId)})` : ''}
            </div>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Qty to transfer (Max: {transferring.qty})</span>
              <input type="number" max={transferring.qty} min="1" value={transferring.transferQty} onChange={(e) => setTransferring({ ...transferring, transferQty: e.target.value })} className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Reason</span>
              <CustomSelect
                value={transferring.reason}
                onChange={(v) => setTransferring({ ...transferring, reason: v, customReason: '' })}
                options={[
                  !hideEmergAndDelivery && { value: 'Emergency product', label: 'Emergency product' },
                  { value: 'Unsold product', label: 'Unsold product' },
                  { value: 'Custom', label: 'Custom' },
                ].filter(Boolean)}
                className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
              />
            </label>
            {transferring.reason === 'Custom' && (
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Custom Reason</span>
                <input type="text" value={transferring.customReason || ''} onChange={(e) => setTransferring({ ...transferring, customReason: e.target.value })} className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
              </label>
            )}
          </div>
          <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={() => setTransferring(null)} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
            <button type="button" onClick={handleTransfer} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>Transfer</button>
          </footer>
        </SideDrawer>
      )}
    </div>
  );
};

InventoryActiveTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  canEdit: PropTypes.bool,
  lockedBranchId: PropTypes.string,
};
